import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Menu, Phone, X } from 'lucide-react';
import { trackEvent, Events } from '../lib/analytics';
import FloatingTryLive from '../components/FloatingTryLive';
import CallForm from '../components/landing/CallForm';
import CallScene from '../components/landing/CallScene';
import { ScrollStatement, StoryChapters, IndustryTabs, FaqList } from '../components/landing/Sections';
import { usePrefersReducedMotion, useRevealRoot, openCallForm } from '../components/landing/motion';
import ThemeSwitch from '../components/landing/ThemeSwitch';
import { ATTRIBUTION_TEXT } from '../components/SiteAttribution';
import { useTheme } from '../contexts/ThemeContext';
import '../components/landing/landing.css';

/* ═══════════════════ SEO structured data ═══════════════════ */
const ORG_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'Organization',
  name: 'Agent9', url: 'https://agent9.me',
  description: 'AI voice agents for local businesses: answer calls, book appointments, transfer customers, and follow up automatically.',
  logo: 'https://agent9.me/assets/agent9-logo.png',
};
const SOFTWARE_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'SoftwareApplication',
  name: 'Agent9', applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web', offers: { '@type': 'Offer', price: '129', priceCurrency: 'USD' },
  description: 'AI-powered phone system for local businesses.',
};
const FAQ_ITEMS = [
  { q: 'What is an AI phone system?', a: 'An AI phone system uses artificial intelligence to answer, route, and manage business calls automatically. It handles inbound calls 24/7, qualifies leads, schedules appointments, and sends follow-up messages, all without a person picking up.' },
  { q: 'Can AI answer calls automatically for my business?', a: 'Yes. When you forward your business line to Agent9, it picks up every call instantly: no hold music, no voicemail. It greets callers professionally, answers their questions, and captures their details.' },
  { q: 'What kind of businesses can use this?', a: 'Agent9 serves salons and spas, restaurants, real estate agents, HVAC and plumbing companies, contractors and remodelers, property managers, and any service business that takes phone calls and books appointments.' },
  { q: 'How does AI scheduling work?', a: 'Agent9 listens for scheduling intent, checks your availability, and books the appointment, showing, or service visit directly into your Google Calendar. It sends confirmation texts and emails automatically.' },
  { q: 'Can it transfer a call to me?', a: 'Yes. When a caller asks for a person, Agent9 bridges the live call to your real number, so you only take the calls that need you.' },
  { q: 'Can it send SMS and email confirmations?', a: 'Yes. After booking an appointment or capturing a lead, Agent9 sends SMS confirmations, appointment reminders, and email summaries to you and your customer.' },
  { q: 'How is this different from a virtual receptionist?', a: 'Agent9 answers every call instantly, 24/7, with zero wait time. It never takes breaks, handles many calls at once, and costs a fraction of a human answering service.' },
  { q: 'Does it integrate with Google Calendar?', a: 'Yes. Agent9 offers one-click Google Calendar connection with two-way sync. Appointments it books appear in your calendar instantly, and your existing events are respected to prevent double-booking.' },
  { q: 'Can I try it for free?', a: 'Yes. Every plan includes a 7-day free trial with AI voice minutes and SMS credits. No credit card is required to start. Plans begin at $129/month.' },
];
const FAQ_SCHEMA = {
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
};

/* ═══════════════════ Content ═══════════════════ */
const INDUSTRY_STRIP = ['Salons & Spas', 'Restaurants', 'Real Estate', 'HVAC', 'Plumbing', 'General Contractors', 'Remodeling'];

const CHAPTERS = [
  { num: '01', label: 'Answer', caption: 'Under a second · day or night', title: 'It picks up on the first ring.', body: 'Every call is answered in under a second, at 2 PM or 2 AM, in a natural voice that sounds like your best front-desk person. No hold music. No voicemail.' },
  { num: '02', label: 'Understand', caption: 'Taught once · by you', title: 'It knows your business.', body: 'You teach it your services, prices, hours, staff, and FAQs once. It answers questions the way you would and recognizes returning callers by name.' },
  { num: '03', label: 'Act', caption: 'Calendar · SMS · live transfer', title: 'It books, texts, and transfers.', body: 'It finds an open slot and books it into your calendar, texts the confirmation, flags emergencies, and bridges the live call to you when a caller asks for a person.' },
  { num: '04', label: 'Report', caption: 'Every call · summarized', title: 'You get the lead before they hang up.', body: 'A summary of every call lands in your inbox, with call history, a lead inbox, and analytics in your dashboard. Setup is just forwarding your line: under 2 minutes.' },
];

const PANELS = [
  <div key="p0" className="grid gap-6">
    <div className="a9l-bars" aria-hidden="true">{Array.from({ length: 28 }).map((_, k) => <i key={k} style={{ '--k': k, height: `${20 + ((k * 37) % 70)}%` }} />)}</div>
    <div>
      <div className="a9l-timer">00:00.4</div>
      <div className="a9l-label mt-2">Time to answer · example</div>
    </div>
  </div>,
  <div key="p1" className="grid gap-5">
    <div className="a9l-chipset">
      {['Services & prices', 'Hours', 'Staff', 'Locations', 'FAQs', 'Your tone', 'Returning callers'].map((c, i) => (
        <span key={c} className={`a9l-chip ${i % 3 !== 2 ? 'a9l-chip--on' : ''}`}>{c}</span>
      ))}
    </div>
    <p className="a9l-body" style={{ fontSize: '0.9rem' }}>“Welcome back, Maria. Same color as last time?”</p>
  </div>,
  <div key="p2" className="grid gap-3">
    <div className="a9l-card flex items-center justify-between gap-4">
      <div>
        <div className="a9l-label">Calendar</div>
        <div className="mt-1 font-medium">Sat · 10:30 AM · Gel fill with Linh</div>
      </div>
      <Check size={18} style={{ color: 'var(--ok)' }} aria-hidden="true" />
    </div>
    <div className="a9l-bubble">You’re booked for Sat at 10:30 AM. Reply C to cancel.</div>
    <div className="a9l-card flex items-center gap-3"><Phone size={16} style={{ color: 'var(--signal)' }} aria-hidden="true" /><span className="text-sm">Transferring the live call to the owner…</span></div>
  </div>,
  <div key="p3" className="a9l-card grid gap-3">
    <div className="a9l-label">Daily summary · example</div>
    {[['Calls answered', '14'], ['Appointments booked', '6'], ['Live transfers', '2'], ['New leads', '5']].map(([k, v]) => (
      <div key={k} className="flex items-center justify-between border-b pb-2" style={{ borderColor: 'var(--line)' }}>
        <span className="text-sm" style={{ color: 'var(--mist)' }}>{k}</span>
        <span style={{ fontFamily: 'var(--f-display)', fontSize: '1.6rem', lineHeight: 1 }}>{v}</span>
      </div>
    ))}
  </div>,
];

const INDUSTRIES = [
  {
    key: 'salons', tab: 'Salons & spas', title: 'A receptionist who never puts a regular on hold.',
    desc: 'Books nails, hair, and lash appointments around your staff’s real schedule, even during your busiest Saturday.',
    points: ['Books by service, stylist, and open slot', 'Recognizes regulars and their usual service', 'Transfers to the owner when a caller asks'],
    convo: [['caller', 'Can I get a gel fill Saturday morning?'], ['agent', 'Saturday at 10:30 is open with Linh. Want me to book it?']],
    outcomes: ['Booked', 'Confirmation texted'],
  },
  {
    key: 'restaurants', tab: 'Restaurants', title: 'Takes the order during the dinner rush.',
    desc: 'Answers hours and menu questions, takes takeout orders, and texts a confirmation, while your team stays on the line.',
    points: ['Takeout orders and pickup times', 'Hours, menu, and location questions', 'SMS confirmation for every order'],
    convo: [['caller', 'Two pad thai for pickup at 7?'], ['agent', 'Got it: two pad thai, ready at 7:00. I’ll text your confirmation now.']],
    outcomes: ['Order taken', 'Text sent'],
  },
  {
    key: 'real-estate', tab: 'Real estate', title: 'Every buyer call becomes a booked showing.',
    desc: 'Qualifies buyers and sellers with the questions you would ask, schedules showings into your Google Calendar, and follows up.',
    points: ['Budget, timeline, and area qualification', 'Showings booked into Google Calendar', 'Hot leads sent to you right away'],
    convo: [['caller', 'Is the house on Maple Street still available?'], ['agent', 'It is. I can show it tomorrow at 5:30. Are you pre-approved yet?']],
    outcomes: ['Lead qualified', 'Showing booked'],
  },
  {
    key: 'home-services', tab: 'HVAC, plumbing & contractors', title: 'The after-hours call you used to lose.',
    desc: 'For HVAC, plumbing, general contractors, and remodelers: it asks the intake questions your trade needs, flags emergencies, and books the visit.',
    points: ['Trade-specific intake questions', 'Emergencies flagged and sent to you', 'Estimates and service visits booked'],
    convo: [['caller', 'Our water heater is leaking into the garage.'], ['agent', 'I’ve marked this urgent. Is the water shut off? I can get a plumber there at 7 AM.']],
    outcomes: ['Emergency flagged', 'Visit booked'],
  },
];

const CAPABILITIES = [
  ['Call answering', 'Every inbound call answered in under a second, 24/7. Natural conversation in a real human voice.'],
  ['Appointment booking', 'Detects intent, checks availability, books the slot, and sends a confirmation in the same breath.'],
  ['Call forwarding', 'When a caller asks for a person, Agent9 bridges the live call to your real number. No drops, no loops.'],
  ['SMS follow-up', 'Confirmations, reminders, and post-call recaps, sent automatically to keep customers in the loop.'],
  ['Customer lookup', 'Recognizes returning callers, greets them by name, and surfaces their last visit.'],
  ['Analytics', 'Call volume, bookings, top questions, and missed-call recovery. Know exactly what your phone is earning.'],
];

const PLANS = [
  { name: 'Starter', price: '$129', sub: 'per month', desc: 'For solo agents and single-location businesses getting started with AI calls.', features: ['AI receptionist 24/7', '500 voice minutes', 'SMS confirmations', 'Google Calendar sync'] },
  { name: 'Pro', price: '$299', sub: 'per month', desc: 'For busy teams that need deeper knowledge, analytics, and lead routing.', features: ['Everything in Starter', '2,000 voice minutes', 'Lead inbox and FAQ analytics', 'Call transfer to staff', 'Priority support'], highlight: true },
  { name: 'Enterprise', price: 'Custom', sub: 'multi-location', desc: 'For franchises and multi-location operators, with per-location seats and dedicated support.', features: ['Unlimited minutes', 'Custom voice and knowledge', 'Dedicated success manager', 'SLA and uptime guarantees'] },
];

const SALES_MAILTO = 'mailto:support@agent9.me?subject=Agent9%20Enterprise%20inquiry';

/* ═══════════════════ Page ═══════════════════ */
export default function LandingPage() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  useRevealRoot(rootRef, reduced);

  const { resolvedTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  // index.html paints the home page's first frame in the right theme; once React
  // has rendered, the scoped html:has(.a9l) rules take over.
  useEffect(() => { document.documentElement.classList.remove('a9-boot-home'); }, []);

  // Browsers honor the first theme-color <meta>, so update index.html's own tag
  // rather than adding a second one; restore the app's value on the way out.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return undefined;
    const previous = meta.getAttribute('content');
    meta.setAttribute('content', resolvedTheme === 'dark' ? '#05060b' : '#f6f4ef');
    return () => meta.setAttribute('content', previous);
  }, [resolvedTheme]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goSignup = (src) => {
    trackEvent(Events.SIGNUP_CLICKED, { source: src });
    trackEvent(Events.LANDING_CTA_CLICK, { source: src, dest: 'signup' });
    navigate('/signup');
  };
  // "Book a demo" goes where the Google Ads sitelink already points.
  const goDemo = (src) => {
    trackEvent(Events.DEMO_MODAL_OPENED, { source: src });
    trackEvent(Events.LANDING_CTA_CLICK, { source: src, dest: 'demo' });
    navigate('/signup?demo=1');
  };
  const tryLive = (src) => {
    trackEvent(Events.LANDING_CTA_CLICK, { source: src, dest: 'live-call' });
    setMenuOpen(false);
    openCallForm(src);
  };
  const goLogin = () => navigate('/login');

  const navLinks = [['#industries', 'Industries'], ['#how-it-works', 'How it works'], ['#features', 'Product'], ['#pricing', 'Pricing'], ['#faq', 'FAQ']];

  return (
    <div ref={rootRef} className="a9l" data-testid="landing-root">
      <Helmet>
        <title>Agent9 | AI Voice Agents That Answer Every Call for Local Businesses</title>
        <meta name="description" content="Agent9 answers every call in under a second, 24/7. It books appointments, texts confirmations, transfers callers, and sends you every lead. For salons, restaurants, real estate, HVAC, plumbing, and contractors." />
        <link rel="canonical" href="https://agent9.me/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://agent9.me/" />
        <meta property="og:title" content="Agent9 | The phone rings. Agent9 answers." />
        <meta property="og:description" content="AI voice agents for local businesses. Every call answered, booked, and followed up, day or night." />
        <meta property="og:image" content="https://agent9.me/assets/icon-512.png" />
        <meta name="twitter:card" content="summary" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Geist+Mono:wght@400;500&display=swap" />
        <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <a href="#main" className="a9l-skip">Skip to content</a>

      {/* ═════════ NAV ═════════ */}
      <header className={`a9l-nav ${scrolled || menuOpen ? 'is-scrolled' : ''}`}>
        <div className="a9l-wrap a9l-nav__bar">
          <a href="#top" className="a9l-brand" data-testid="landing-nav-brand" aria-label="Agent9 home">
            <img src="/assets/apple-touch-icon.png" alt="" width="28" height="28" />
            <span>Agent9</span>
          </a>
          <nav className="a9l-nav__links" aria-label="Primary">
            {navLinks.map(([href, label]) => (
              <a key={href} href={href} data-testid={`nav-${href.slice(1)}`}>{label}</a>
            ))}
          </nav>
          <div className="a9l-nav__actions">
            <ThemeSwitch />
            <button type="button" onClick={goLogin} data-testid="nav-login-btn" className="a9l-btn a9l-btn--quiet a9l-nav__login">Log in</button>
            <button type="button" onClick={() => goSignup('nav')} data-testid="nav-start-trial-btn" className="a9l-btn a9l-btn--signal a9l-nav__cta">Start free trial</button>
            <button
              type="button"
              className="a9l-menu-btn"
              aria-expanded={menuOpen}
              aria-controls="a9l-mobile-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              data-testid="nav-menu-btn"
              onClick={() => setMenuOpen((m) => !m)}
            >
              {menuOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav id="a9l-mobile-menu" className="a9l-mobile-menu" aria-label="Mobile">
            {navLinks.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}</a>
            ))}
            <ThemeSwitch variant="row" />
            <button type="button" onClick={goLogin}>Log in</button>
          </nav>
        )}
      </header>

      <main id="main">
        {/* ═════════ HERO ═════════ */}
        <section id="top" className="a9l-hero" aria-labelledby="hero-headline">
          <div className="a9l-hero__light" aria-hidden="true" />
          <div className="a9l-hero__horizon" aria-hidden="true" />
          <div className="a9l-wrap a9l-hero__grid">
            <div>
              <p className="a9l-label a9l-kicker a9l-label--signal a9l-fade-in" style={{ '--delay': '0ms' }} data-testid="hero-badge">
                Voice AI for local businesses
              </p>
              <h1 id="hero-headline" data-testid="hero-headline" className="a9l-display mt-6">
                <span className="a9l-line"><span style={{ '--i': 0 }}>The phone rings.</span></span>
                <span className="a9l-line"><span style={{ '--i': 1 }}><em>Agent9 answers.</em></span></span>
              </h1>
              <p data-testid="hero-subheadline" className="a9l-lede mt-7 max-w-xl a9l-fade-in" style={{ '--delay': '450ms' }}>
                Agent9 picks up every call in under a second, day or night. It books the appointment,
                texts the confirmation, and hands you the lead before the caller hangs up.
              </p>
              <div className="a9l-hero__ctas a9l-fade-in" style={{ '--delay': '600ms' }}>
                <button type="button" className="a9l-btn a9l-btn--signal" data-testid="hero-try-demo-btn" onClick={() => tryLive('hero')}>
                  <Phone size={16} aria-hidden="true" /> Get a live call from Agent9
                </button>
                <button type="button" className="a9l-btn a9l-btn--ghost" data-testid="hero-start-trial-btn" onClick={() => goSignup('hero')}>
                  Start free trial <ArrowRight size={16} className="a9l-arrow" aria-hidden="true" />
                </button>
              </div>
              <p className="a9l-hero__fine a9l-fade-in" style={{ '--delay': '700ms' }}>7-day free trial · No credit card · Plans from $129/mo</p>

              <dl className="a9l-hero__proof a9l-fade-in" style={{ '--delay': '850ms' }}>
                {[['< 1s', 'to answer'], ['24/7', 'always on'], ['30+', 'salons live']].map(([n, l]) => (
                  <div key={l}><dt className="a9l-sr">{l}</dt><dd><div className="a9l-proof__n">{n}</div><div className="a9l-proof__l">{l}</div></dd></div>
                ))}
              </dl>
            </div>

            <div className="a9l-fade-in" style={{ '--delay': '300ms' }}>
              <CallScene reduced={reduced} />
            </div>
          </div>
        </section>

        <div className="a9l-wrap">
          <div className="a9l-strip" data-testid="industry-strip" aria-label="Industries Agent9 is built for">
            <span className="a9l-label">Built for:</span>
            {INDUSTRY_STRIP.map((label) => (
              <span key={label} className="a9l-strip__chip" data-testid={`industry-chip-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{label}</span>
            ))}
          </div>
        </div>

        {/* ═════════ ACT I: the stakes ═════════ */}
        <section className="a9l-section" aria-label="Why it matters">
          <div className="a9l-wrap">
            <p className="a9l-label a9l-kicker mb-8">After hours</p>
            <ScrollStatement
              reduced={reduced}
              text="Every call that goes to voicemail is a customer deciding to call the next name on the list. Agent9 makes sure it’s never you."
              accent={[18, 19, 20, 21, 22, 23]}
            />
          </div>
        </section>

        {/* ═════════ ACT II: one call, four moves ═════════ */}
        <section id="how-it-works" className="a9l-section a9l-section--rule" aria-labelledby="how-title">
          <span id="how" aria-hidden="true" />
          <div className="a9l-wrap">
            <div className="a9l-head a9l-head--split" data-reveal>
              <div>
                <p className="a9l-label a9l-kicker a9l-label--signal">How it works</p>
                <h2 id="how-title" className="a9l-h2 mt-5">One call. <em>Four moves.</em></h2>
              </div>
              <p className="a9l-lede">Forward your line to your Agent9 number and every call follows the same path, from the first ring to the lead in your inbox.</p>
            </div>
            <StoryChapters chapters={CHAPTERS} panels={PANELS} />
          </div>
        </section>

        {/* ═════════ ACT III: your business ═════════ */}
        <section id="industries" className="a9l-section a9l-section--rule" aria-labelledby="ind-title">
          <span id="use-cases" aria-hidden="true" />
          <div className="a9l-wrap">
            <div className="a9l-head" data-reveal>
              <p className="a9l-label a9l-kicker a9l-label--signal">Industries</p>
              <h2 id="ind-title" className="a9l-h2">Trained for the calls <em>your</em> business gets.</h2>
            </div>
            <div data-reveal>
              <IndustryTabs items={INDUSTRIES} onTry={tryLive} />
            </div>
          </div>
        </section>

        {/* ═════════ Capabilities ═════════ */}
        <section id="features" className="a9l-section a9l-section--rule" aria-labelledby="features-title">
          <div id="products" className="a9l-wrap">
            <div className="a9l-head a9l-head--split" data-reveal>
              <div>
                <p className="a9l-label a9l-kicker a9l-label--signal">Product</p>
                <h2 id="features-title" className="a9l-h2 mt-5">Everything your front desk does, <em>automated.</em></h2>
              </div>
              <p className="a9l-lede">One agent that answers, books, follows up, and reports. No apps for your callers to install. Nothing new for them to learn.</p>
            </div>
            <ol className="a9l-index">
              {CAPABILITIES.map(([title, desc], i) => (
                <li key={title} className="a9l-row" data-reveal style={{ '--rd': `${i * 60}ms` }} data-testid={`product-${title.toLowerCase().replace(/\s/g, '-')}`}>
                  <span className="a9l-label">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="a9l-row__title">{title}</h3>
                  <p className="a9l-body">{desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ═════════ The conversion moment ═════════ */}
        <section id="live-demo" className="a9l-section a9l-live" aria-labelledby="live-title" data-testid="mid-page-cta">
          <div className="a9l-wrap a9l-live__grid">
            <div data-reveal>
              <p className="a9l-label a9l-kicker a9l-label--signal">Live demo</p>
              <h2 id="live-title" className="a9l-h2 mt-5">Experience Your Own <em>AI Receptionist.</em></h2>
              <p className="a9l-lede mt-6 max-w-xl">
                Enter your business website and hear how an AI assistant could answer calls for your company.
                Our AI learns your website, then calls you as your business's assistant. Ask about your services,
                prices or hours, or try to book. Treat it like one of your customers would.
              </p>
            </div>
            <div id="live-demo-form" className="a9l-formcard" data-reveal style={{ '--rd': '120ms' }}>
              <CallForm source="live-demo-section" testIdPrefix="live-demo" />
            </div>
          </div>
        </section>

        {/* ═════════ Pricing ═════════ */}
        <section id="pricing" className="a9l-section" aria-labelledby="pricing-title">
          <div className="a9l-wrap">
            <div className="a9l-head a9l-head--split" data-reveal>
              <div>
                <p className="a9l-label a9l-kicker a9l-label--signal">Pricing</p>
                <h2 id="pricing-title" className="a9l-h2 mt-5">Less than one missed customer <em>a month.</em></h2>
              </div>
              <p className="a9l-lede">Simple plans with a 7-day free trial. No credit card to start. Custom plans for high-volume and multi-location teams.</p>
            </div>
            <div className="a9l-prices" data-reveal>
              {PLANS.map((p) => (
                <article key={p.name} className={`a9l-price ${p.highlight ? 'a9l-price--hi' : ''}`} data-testid={`pricing-${p.name.toLowerCase()}`} aria-label={`${p.name} plan`}>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold">{p.name}</h3>
                    {p.highlight && <span className="a9l-label a9l-label--signal">Most popular</span>}
                  </div>
                  <div className="a9l-price__amt">{p.price}<span className="a9l-note" style={{ fontFamily: 'var(--f-body)', fontSize: '0.9rem', marginLeft: '0.5rem' }}>/ {p.sub}</span></div>
                  <p className="a9l-body mt-4" style={{ fontSize: '0.92rem' }}>{p.desc}</p>
                  <ul>
                    {p.features.map((f) => (
                      <li key={f}><Check size={15} aria-hidden="true" /><span>{f}</span></li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    {p.name === 'Enterprise' ? (
                      <a href={SALES_MAILTO} className="a9l-btn a9l-btn--ghost w-full" data-testid="pricing-enterprise-cta" onClick={() => trackEvent(Events.LANDING_CTA_CLICK, { source: 'pricing-Enterprise', dest: 'sales-email' })}>
                        Contact sales <ArrowRight size={14} className="a9l-arrow" aria-hidden="true" />
                      </a>
                    ) : (
                      <button type="button" onClick={() => goSignup(`pricing-${p.name}`)} data-testid={`pricing-${p.name.toLowerCase()}-cta`} className={`a9l-btn w-full ${p.highlight ? 'a9l-btn--signal' : 'a9l-btn--ghost'}`}>
                        Start free trial
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
            <p className="a9l-note mt-6">Want a walkthrough first? <button type="button" className="a9l-inline-link" data-testid="pricing-book-demo-link" onClick={() => goDemo('pricing')}>Book a demo</button>.</p>
          </div>
        </section>

        {/* ═════════ FAQ ═════════ */}
        <section id="faq" className="a9l-section a9l-section--rule" aria-labelledby="faq-title">
          <div className="a9l-wrap" style={{ maxWidth: '64rem' }}>
            <div className="a9l-head" data-reveal>
              <p className="a9l-label a9l-kicker a9l-label--signal">FAQ</p>
              <h2 id="faq-title" className="a9l-h2">Questions, <em>answered.</em></h2>
            </div>
            <FaqList items={FAQ_ITEMS} />
          </div>
        </section>

        {/* ═════════ Finale ═════════ */}
        <section id="get-started" className="a9l-section a9l-finale a9l-section--rule" aria-labelledby="finale-title">
          <div className="a9l-wrap" data-reveal>
            <p className="a9l-label">Tonight, 9:47 PM</p>
            <h2 id="finale-title" className="a9l-display mt-6 mx-auto" style={{ maxWidth: '14ch', fontSize: 'clamp(2.8rem, 6.4vw, 6rem)' }}>
              Never send a customer to <em>voicemail</em> again.
            </h2>
            <div className="a9l-hero__ctas justify-center">
              <button type="button" className="a9l-btn a9l-btn--signal" data-testid="final-start-trial-btn" onClick={() => goSignup('final')}>
                Start your free trial <ArrowRight size={16} className="a9l-arrow" aria-hidden="true" />
              </button>
              <button type="button" className="a9l-btn a9l-btn--ghost" data-testid="final-try-live-btn" onClick={() => tryLive('final')}>
                <Phone size={16} aria-hidden="true" /> Hear it live first
              </button>
            </div>
            <p className="a9l-hero__fine">7 days free. No credit card. Live in minutes.</p>
          </div>
        </section>
      </main>

      {/* ═════════ Footer ═════════ */}
      <footer id="site-footer" className="a9l-foot">
        <div className="a9l-wrap">
          <div className="a9l-foot__grid">
            <div className="col-span-2 md:col-span-1">
              <span className="a9l-brand"><img src="/assets/apple-touch-icon.png" alt="" width="28" height="28" loading="lazy" /><span>Agent9</span></span>
              <p className="a9l-body mt-4 max-w-xs" style={{ fontSize: '0.9rem' }}>AI voice agents for local businesses. Answer every call. Book every appointment. Never miss a lead.</p>
            </div>
            <div>
              <h3>Product</h3>
              <ul>
                <li><a href="#features" data-testid="footer-product-features">Features</a></li>
                <li><a href="#pricing" data-testid="footer-product-pricing">Pricing</a></li>
                <li><a href="#live-demo" data-testid="footer-product-demo">Live demo</a></li>
              </ul>
            </div>
            <div>
              <h3>Industries</h3>
              <ul>
                <li><a href="#industries" data-testid="footer-ind-salon">Salons</a></li>
                <li><a href="#industries" data-testid="footer-ind-restaurant">Restaurants</a></li>
                <li><a href="#industries" data-testid="footer-ind-real-estate">Real estate</a></li>
                <li><a href="#industries" data-testid="footer-ind-home-services">HVAC & plumbing</a></li>
              </ul>
            </div>
            <div>
              <h3>Company</h3>
              <ul>
                <li><a href="/blog" data-testid="footer-res-blog">Blog</a></li>
                <li><a href="/terms" data-testid="footer-terms">Terms</a></li>
                <li><a href="/privacy" data-testid="footer-privacy">Privacy</a></li>
                <li><button type="button" onClick={goLogin} data-testid="footer-res-login">Log in</button></li>
              </ul>
            </div>
          </div>
          <div className="mt-14 pt-6 flex flex-col sm:flex-row justify-between gap-3 a9l-note" style={{ borderTop: '1px solid var(--line)' }}>
            <span>© {new Date().getFullYear()} Agent9. All rights reserved.</span>
            <a href="mailto:support@agent9.me" className="a9l-inline-link" data-testid="footer-support-email">support@agent9.me</a>
          </div>
          <p className="a9l-attrib" data-testid="site-attribution-inline">{ATTRIBUTION_TEXT}</p>
        </div>
      </footer>

      <FloatingTryLive heroId="top" hideOver={['live-demo-form', 'get-started', 'site-footer']} />
    </div>
  );
}
